import { useMemo } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface PasswordStrengthIndicatorProps {
  password: string;
}

const requirements = [
  { key: "length", label: "At least 8 characters", test: (p: string) => p.length >= 8 },
  { key: "uppercase", label: "One uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { key: "lowercase", label: "One lowercase letter", test: (p: string) => /[a-z]/.test(p) },
  { key: "number", label: "One number", test: (p: string) => /[0-9]/.test(p) },
  { key: "special", label: "One special character (!@#...)", test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

export const validatePassword = (password: string): string | null => {
  for (const req of requirements) {
    if (!req.test(password)) {
      return `Password must have: ${req.label.toLowerCase()}`;
    }
  }
  return null;
};

const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  const checks = useMemo(
    () => requirements.map((r) => ({ ...r, passed: r.test(password) })),
    [password]
  );

  if (!password) return null;

  return (
    <ul className="mt-2 space-y-1">
      {checks.map((check) => (
        <li
          key={check.key}
          className={cn(
            "flex items-center gap-2 text-xs transition-colors duration-200",
            check.passed ? "text-green-400" : "text-muted-foreground"
          )}
        >
          <div
            className={cn(
              "w-4 h-4 rounded-full flex items-center justify-center border transition-colors duration-200",
              check.passed
                ? "border-green-400 bg-green-400/20"
                : "border-muted-foreground/40 bg-transparent"
            )}
          >
            {check.passed && <Check className="w-3 h-3" />}
          </div>
          {check.label}
        </li>
      ))}
    </ul>
  );
};

export default PasswordStrengthIndicator;
